import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import Typography, {
  TypographyProps as MUITypographyProps,
} from '@material-ui/core/Typography';

import * as React from 'react';
import cx from 'classnames';

const commonStyles = (t: Theme) =>
  createStyles({
    uppercase: {
      textTransform: 'uppercase',
    },
    'overflow-visible': {
      overflow: 'hidden',
      whiteSpace: 'initial',
    },
    'overflow-initial': {
      overflow: 'hidden',
      textOverflow: 'initial',
    },
    'overflow-ellipsis': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    'overflow-clip': {
      overflow: 'hidden',
      textOverflow: 'clip',
    },
    'color-default': {
      color: t.palette.darkBlue.main,
    },
    'color-muted': {
      color: t.palette.darkBlue.light,
    },
    'color-mint': {
      color: t.palette.mint.main,
    },
    'color-error': {
      color: t.palette.error.main,
    },
    'color-white': {
      color: t.palette.white.main,
    },
    // custom style should be removed when we update typography to >=3.9
    // so that we will be able to simply pass `inline` prop to MUI component
    // https://github.com/mui-org/material-ui/blob/next/CHANGELOG.md#material-uicorev390
    inline: {
      display: 'inline',
    },
  });

const H1Styles = (t: Theme) => ({
  ...createStyles({
    ...commonStyles(t),
    root: {
      fontWeight: 'bold',
      fontSize: '24px',
      [t.breakpoints.up('md')]: {
        fontSize: '30px',
      },
    },
  }),
});

const H1LightStyles = (t: Theme) => ({
  ...createStyles({
    ...commonStyles(t),
    root: {
      color: '#808F9A', // grey color 300
      fontSize: '24px',
      [t.breakpoints.up('md')]: {
        fontSize: '30px',
      },
    },
  }),
});

const CH1Styles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'bold',
      fontSize: '24px',
      [t.breakpoints.up('md')]: {
        fontSize: '30px',
      },
      color: t.palette.white.main,
      backgroundColor: t.palette.darkBlue.main,
      padding: '1rem',
    },
  }),
});

const H2Styles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'bold',
      fontSize: '18px',
      [t.breakpoints.up('md')]: {
        fontSize: '24px',
      },
    },
  }),
});

const H3Styles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'bold',
      fontSize: '16px',
      [t.breakpoints.up('md')]: {
        fontSize: '20px',
      },
    },
  }),
});

const H4Styles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'bold',
      fontSize: '14px',
      [t.breakpoints.up('md')]: {
        fontSize: '18px',
      },
    },
  }),
});

const ParagraphTitleStyles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'bold',
      fontSize: '16px',
      lineHeight: '26px',
    },
  }),
});

const ParagraphStyles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'normal',
      fontSize: '16px',
      lineHeight: '1.5rem',
    },
  }),
});

const CaptionStyles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'normal',
      fontSize: '14px',
    },
  }),
});

const helperStyles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'normal',
      fontSize: '12px',
      lineHeight: '16px',
    },
  }),
});

const ButtonStyles = (t: Theme) => ({
  ...commonStyles(t),
  ...createStyles({
    root: {
      fontWeight: 'normal',
      fontSize: '14px',
      textTransform: 'uppercase',
    },
  }),
});

export type Color = 'muted' | 'mint' | 'error' | 'white' | 'default';
export type Overflow = 'ellipsis' | 'clip' | 'initial' | 'visible';
export type Align = MUITypographyProps['align'];

type ComponentType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'span'
  | 'div'
  | 'p';

interface TypographyProps {
  color?: Color;
  uppercase?: boolean;
  noWrap?: boolean;
  overflow?: Overflow;
  component?: ComponentType;
  align?: Align;
  inline?: boolean;
  className?: string;
}

type Styles = typeof H1Styles;
type Props = TypographyProps & WithStyles<Styles>;

type TypographyVariant =
  | 'display1'
  | 'headline'
  | 'title'
  | 'subheading'
  | 'body2'
  | 'body1'
  | 'caption'
  | 'button';

type Typography = (variant: TypographyVariant) => React.SFC<Props>;

// Typography: https://housinganywhere.atlassian.net/wiki/spaces/DS/pages/914489352/Typography
// Description: This component is in charge of setting up predifined styles from DSL and
// allowing you to choose between default and muted color text.
const CustomTypography: Typography = variant => ({
  className,
  uppercase,
  classes,
  children,
  noWrap,
  component,
  color = 'default',
  overflow = 'initial',
  align,
  inline,
  ...rest
}) => (
  <Typography
    className={cx(
      className,
      classes.root,
      classes[`overflow-${overflow}`],
      classes[`color-${color}`],
      { [classes.uppercase]: uppercase },
      { [classes.inline]: inline },
    )}
    component={component}
    noWrap={
      overflow !== 'visible' &&
      (noWrap || overflow === 'ellipsis' || overflow === 'clip')
    }
    variant={variant}
    align={align}
    {...rest}
  >
    {children}
  </Typography>
);

function make<S extends Styles>(variant: TypographyVariant, styles: S) {
  return withStyles(styles)(CustomTypography(variant));
}

// We expose different typographies through Typography component
// so you can use Typography.Variant
// Different styles can be applied in the future altough right now
// it is about color, but we could manage font size by breakpoints.
export default {
  H1: make('display1', H1Styles),
  H1Light: make('display1', H1LightStyles),
  CH1: make('display1', CH1Styles),
  H2: make('headline', H2Styles),
  H3: make('title', H3Styles),
  H4: make('subheading', H4Styles),
  ParagraphTitle: make('body1', ParagraphTitleStyles),
  Paragraph: make('body2', ParagraphStyles),
  Caption: make('caption', CaptionStyles),
  HelperText: make('caption', helperStyles),
  Button: make('button', ButtonStyles),
};
